import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../shared/services/auth.service';
import { RegisterUserDto, UserTypeItem } from '../../shared/models/auth.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnauthorizedLayoutComponent } from "../../shared/components/unauthorized-layout/unauthorized-layout.component";

@Component({
  selector: 'app-registration',
  standalone: true,
  templateUrl: './registration.html',
  styleUrls: ['./registration.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    TranslateModule,
    UnauthorizedLayoutComponent
  ]
})
export class RegistrationComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isLoading = false;
  isSubmitted = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  fieldErrors: { [key: string]: string[] } = {};
  selectedFile: File | null = null;
  selectedFileName: string = '';
  userTypes: UserTypeItem[] = [];
  isLoadingUserTypes = true;
  showPassword = false;
  showConfirmPassword = false;
  passwordStrength: { hasNumber: boolean; hasLetter: boolean; hasSpecial: boolean } = {
    hasNumber: false,
    hasLetter: false,
    hasSpecial: false
  };
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private translate: TranslateService
  ) {
    this.form = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(8), this.passwordStrengthValidator.bind(this)]],
      confirmPassword: ['', [Validators.required]],
      fullName: ['', [Validators.required, Validators.maxLength(255)]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(8), Validators.pattern(/^[0-9]+$/)]],
      position: ['', [Validators.required, Validators.maxLength(255)]],
      organization: ['', [Validators.required, Validators.maxLength(255)]],
      justification: ['', [Validators.required, Validators.maxLength(2000)]],
      userTypeId: [{ value: '', disabled: true }, Validators.required],
      idAttachment: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    // Fetch user types
    this.fetchUserTypes();

    // Subscribe to auth state
    this.authService.authState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.isLoading = state.loading;
      });

    // If already logged in, redirect
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/file-management']);
    }
  }

  /**
   * Fetch user types from backend
   */
  private fetchUserTypes(): void {
    this.isLoadingUserTypes = true;
    this.authService.getUserTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (userTypes) => {
          this.userTypes = userTypes;
          this.isLoadingUserTypes = false;
          
          // Enable userTypeId control after loading
          this.form.get('userTypeId')?.enable();
          
          // Set first user type as default if available
          if (userTypes.length > 0) {
            this.form.patchValue({ userTypeId: userTypes[0].id });
          }
        },
        error: (error) => {
          console.error('Failed to fetch user types:', error);
          this.isLoadingUserTypes = false;
          this.errorMessage = 'Failed to load user types. Please refresh the page.';
        }
      });
  }

  /**
   * Validator to check password strength
   * Must contain: number, letter, and special character
   */
  private passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const hasNumber = /[0-9]/.test(value);
    const hasLetter = /[a-zA-Z]/.test(value);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);

    // Update strength indicator
    this.passwordStrength = { hasNumber, hasLetter, hasSpecial };

    if (!hasNumber || !hasLetter || !hasSpecial) {
      return {
        weakPassword: {
          hasNumber,
          hasLetter,
          hasSpecial
        }
      };
    }
    return null;
  }

  /**
   * Validator to check if passwords match
   */
  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    if (!password || !confirmPassword) {
      return null;
    }

    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  onFileChange(event: any) {
    const file = event.target.files?.[0] || null;
    if (file) {
      // Validate file type and size
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        this.fieldErrors['id-attachment'] = ['File must be PDF, JPG, JPEG, or PNG'];
        this.selectedFile = null;
        this.form.get('idAttachment')?.reset();
        return;
      }

      if (file.size > maxSize) {
        this.fieldErrors['id-attachment'] = ['File size must not exceed 5MB'];
        this.selectedFile = null;
        this.form.get('idAttachment')?.reset();
        return;
      }

      this.fieldErrors['id-attachment'] = [];
      this.selectedFile = file;
      this.selectedFileName = file.name;
      // Set form control value to mark field as touched and valid
      this.form.get('idAttachment')?.setValue(file);
      this.form.get('idAttachment')?.markAsTouched();
    }
  }

  onSubmit() {
    // Check for file validation errors
    if (this.fieldErrors['id-attachment'] && this.fieldErrors['id-attachment'].length > 0) {
      this.errorMessage = `File validation error: ${this.fieldErrors['id-attachment'][0]}`;
      return;
    }

    if (this.form.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    this.errorMessage = null;
    this.successMessage = null;
    this.fieldErrors = {};

    this.isLoading = true;

    const formData: RegisterUserDto = {
      username: this.form.value.email, // Use email as username
      password: this.form.value.password,
      fullName: this.form.value.fullName,
      email: this.form.value.email,
      phoneNumber: this.form.value.phoneNumber,
      position: this.form.value.position,
      organization: this.form.value.organization,
      justification: this.form.value.justification,
      userTypeId: parseInt(this.form.value.userTypeId, 10),
      identityAttachment: this.selectedFile as File
    };

    this.authService.register(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSubmitted = true;
          
          // Check if OTP email verification was sent
          if (response.success && response.data.isOtpEmailVerificationSent) {
            // Store registration info for verification page
            sessionStorage.setItem('verificationToken', response.data.verificationToken);
            sessionStorage.setItem('otpMessage', response.data.otpVerificationMessage);
            sessionStorage.setItem('otpExpiresAt', response.data.otpExpiresAt);
            
            this.successMessage = `A verification code has been sent to ${response.data.email}. Please verify your email to complete registration.`;
            
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 4000);
          } else {
            // Fallback: if OTP not sent for some reason
            this.successMessage = 'You will receive an email with verification instructions shortly, if you do not see it, please contact the support team.';
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 3000);
          }
        },
        error: (error: any) => {
          console.error('Registration failed:', error);
          this.isLoading = false;
          
          if (error?.errors && Array.isArray(error.errors)) {
            this.fieldErrors = {};
            error.errors.forEach((errMsg: string) => {
              const fieldMatch = errMsg.match(/^(\w+)\s/);
              const field = fieldMatch ? fieldMatch[1].toLowerCase() : 'general';
              
              if (!this.fieldErrors[field]) {
                this.fieldErrors[field] = [];
              }
              this.fieldErrors[field].push(errMsg);
            });
          } else {
            let errorMsg = error?.message || 'Registration failed. Please try again.';
            this.errorMessage = errorMsg;
          }
        }
      });
  }

  getFieldError(fieldName: string): string | null {
    // Convert kebab-case to camelCase for form control lookup
    const camelCaseFieldName = fieldName.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    // Convert kebab-case to title case with spaces for display
    const displayFieldName = fieldName.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    
    const control = this.form.get(camelCaseFieldName);
    if (!control || !control.touched) return null;

    if (this.fieldErrors[camelCaseFieldName]?.length > 0) {
      return this.fieldErrors[camelCaseFieldName][0];
    }

    if (control.hasError('required')) {
      return this.translate.instant('validation.required');
    }

    if (control.hasError('minlength')) {
      const minLength = control.getError('minlength').requiredLength;
      return this.translate.instant('validation.minLength', { field: displayFieldName, length: minLength });
    }

    if (control.hasError('maxlength')) {
      const maxLength = control.getError('maxlength').requiredLength;
      return this.translate.instant('validation.maxLength', { field: '', length: maxLength });
    }

    if (control.hasError('email')) {
      return this.translate.instant('validation.invalidEmail');
    }

    if (control.hasError('pattern')) {
      if (camelCaseFieldName === 'phoneNumber') {
        return this.translate.instant('validation.invalidPhoneNumber');
      }
      return this.translate.instant('validation.invalidPattern', { field: displayFieldName });
    }

    if (control.hasError('weakPassword')) {
      return this.translate.instant('validation.weakPassword');
    }

    return null;
  }

  /**
   * Get error for confirm password field
   * Removed - now using getFieldError('confirm-password') instead
   */

  /**
   * Trigger file input click
   */
  triggerFileUpload(): void {
    const fileInput = document.getElementById('idAttachment') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

